import { useState, useCallback, useRef, useEffect } from 'react';
import emailApi from '../services/emailApi';
import { SPAM_SEED, isSpam, loadSpamRules } from '../utils/spam';

const DEFAULT_FOLDERS = [
  { name: 'inbox', label: 'Inbox', icon: 'inbox', system_folder: 'inbox', order: 0 },
  { name: 'starred', label: 'Starred', icon: 'star', system_folder: 'starred', order: 1 },
  { name: 'snoozed', label: 'Snoozed', icon: 'clock', system_folder: 'snoozed', order: 2 },
  { name: 'sent', label: 'Sent', icon: 'send', system_folder: 'sent', order: 3 },
  { name: 'drafts', label: 'Drafts', icon: 'file-text', system_folder: 'drafts', order: 4 },
  { name: 'archive', label: 'Archive', icon: 'archive', system_folder: 'archive', order: 5 },
  { name: 'spam', label: 'Spam', icon: 'shield-alert', system_folder: 'spam', order: 6 },
  { name: 'junk', label: 'Junk', icon: 'alert-triangle', system_folder: 'junk', order: 7 },
  { name: 'trash', label: 'Trash', icon: 'trash-2', system_folder: 'trash', order: 8 },
];

// Outlook's internal/system folders that should never appear in the sidebar.
const HIDDEN_FOLDER_NAMES = new Set([
  'sync issues', 'conflicts', 'local failures', 'server failures',
  'conversation history', 'outbox', 'rss subscriptions', 'rss feeds',
  'quick step settings', 'journal', 'notes', 'yammer root', 'files',
  'the file', 'suggested contacts', 'contacts', 'calendar', 'tasks',
]);
const isHiddenFolder = f =>
  HIDDEN_FOLDER_NAMES.has(String(f?.name || '').trim().toLowerCase());

const getMessageKeys = message => [
  message?.id,
  message?.message_id,
  message?.thread_id,
  message?.conversation_id,
].filter(value => value !== undefined && value !== null && value !== '')
  .map(String);

export function useEmailState() {
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState(null);
  const [folders, setFolders] = useState(DEFAULT_FOLDERS);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [activeMessage, setActiveMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const MESSAGES_PAGE_SIZE = 25;
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeData, setComposeData] = useState(null);
  const [replyData, setReplyData] = useState(null);
  const [forwardData, setForwardData] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [stats, setStats] = useState({ total: 0, unread: 0, flagged: 0, starred: 0 });
  const [labels, setLabels] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [showReadingPane, setShowReadingPane] = useState(true);
  const [splitPane, setSplitPane] = useState(true);
  const [contextMenu, setContextMenu] = useState(null);
  const [toast, setToast] = useState(null);
  const [newMailNotice, setNewMailNotice] = useState(null);
  const [folderSyncing, setFolderSyncing] = useState(false);

  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);
  const syncedAccountsRef = useRef(new Set());
  const activeFolderRef = useRef('inbox');
  const activeAccountIdRef = useRef(null);
  const readStateOverridesRef = useRef(new Set());
  // Per "account:folder" sync bookkeeping — lets multiple mailboxes sync fully
  // independently and stops rapid folder/mailbox switching from stacking up
  // duplicate deep-sync requests for the same target.
  const inFlightSyncRef = useRef(new Set());
  const lastDeepSyncRef = useRef(new Map());
  const DEEP_SYNC_COOLDOWN_MS = 20000; // matches the 20s live-poll cadence

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type, id: Date.now() });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await emailApi.getAccounts();
      let data = res.data.results || res.data || [];

      // No mailbox connected yet -> try to open the logged-in user's account
      // automatically using the SMTP credentials saved on the settings page.
      // Skip this if the user has explicitly logged out (stay logged out).
      let loggedOut = false;
      try { loggedOut = sessionStorage.getItem('em-mailbox-loggedout') === '1'; } catch {}
      if (data.length === 0 && !loggedOut) {
        try {
          const setup = await emailApi.autoSetupAccount();
          const acct = setup?.data?.account;
          if (acct) {
            data = [acct];
          } else if (setup?.data?.reason === 'no_smtp_credentials') {
            showToast('No email account configured. Add credentials in Email Settings to open your mailbox.', 'error');
          }
        } catch (e) {
          console.error('Auto-setup failed', e);
        }
      }

      setAccounts(data);
      if (data.length > 0 && !activeAccount) {
        setActiveAccount(data[0]);
      }
    } catch (err) {
      console.error('Failed to fetch accounts', err);
    }
  }, [activeAccount, showToast]);

  const fetchFolders = useCallback(async accountId => {
    if (!accountId) return;
    try {
      const res = await emailApi.getFolders(accountId);
      const rawData = res.data.results || res.data || [];
      // Drop Outlook's internal/system folders (Sync Issues, Conflicts, Outbox, …).
      const data = rawData.filter(f => !isHiddenFolder(f));
      if (data.length > 0) {
        const baseFolders = DEFAULT_FOLDERS.map(folder => ({ ...folder }));
        data.forEach(f => {
          // System folders dedupe by their slug; custom/user-created folders
          // (blank system_folder) dedupe by name so several can coexist.
          const dup = f.system_folder
            ? baseFolders.find(bf => bf.system_folder === f.system_folder)
            : baseFolders.find(bf => bf.name === f.name);
          if (!dup) {
            baseFolders.push({
              name: f.name,
              label: f.name.charAt(0).toUpperCase() + f.name.slice(1),
              icon: 'folder',
              system_folder: f.system_folder,
              order: f.order,
              folder_id: f.folder_id,
              parent: f.parent,
              is_collapsible: f.is_collapsible,
              unread_count: f.unread_count,
              total_count: f.total_count,
              children: f.children,
            });
          } else {
            Object.assign(dup, {
              folder_id: f.folder_id || f.id || dup.folder_id,
              order: f.order ?? dup.order,
              unread_count: f.unread_count,
              total_count: f.total_count,
              parent: f.parent,
            });
          }
        });
        baseFolders.sort((a, b) => (a.order || 99) - (b.order || 99));
        setFolders(baseFolders);
      }
    } catch (err) {
      console.error('Failed to fetch folders', err);
    }
  }, []);

  const fetchLabels = useCallback(async accountId => {
    if (!accountId) return;
    try {
      const res = await emailApi.getLabels(accountId);
      setLabels(res.data.results || res.data || []);
    } catch (err) {
      console.error('Failed to fetch labels', err);
    }
  }, []);

  const fetchMessages = useCallback(async (folder, accountId, page = 1, append = false) => {
    const spamRules = loadSpamRules();

    // ── Spam folder: a client-side view over the inbox ───────────────────────
    // Seeded demo spam + any inbox message whose sender/subject matches a spam
    // rule. (There is no server-side spam folder, so we derive it here.)
    if (folder === 'spam') {
      setLoading(true);
      try {
        const params = { page: 1, page_size: 100, folder: 'inbox', deleted: 'false', spam: 'false' };
        if (accountId) params.account = accountId;
        const res = await emailApi.getMessages(params);
        const inbox = res.data.results || res.data || [];
        const matched = inbox.filter(m => isSpam(m, spamRules));
        const all = [...SPAM_SEED, ...matched].map(message =>
          getMessageKeys(message).some(key => readStateOverridesRef.current.has(key))
            ? { ...message, is_read: true, unread_count: 0 }
            : message
        );
        setMessages(all);
        setMessagesTotal(all.length);
        setMessagesPage(1);
        hasMoreRef.current = false;
      } catch (err) {
        setMessages(SPAM_SEED);
        setMessagesTotal(SPAM_SEED.length);
      } finally {
        setLoading(false);
      }
      return;
    }

    const folderParam = folder === 'starred' ? undefined : folder;
    const params = { page, page_size: MESSAGES_PAGE_SIZE };
    if (folderParam) params.folder = folderParam;
    if (folder === 'inbox') {
      params.deleted = 'false';
      params.spam = 'false';
    }
    if (folder === 'trash') params.deleted = 'true';
    if (folder === 'junk') params.spam = 'true';
    if (folder === 'starred') params.starred = 'true';
    if (accountId) params.account = accountId;

    setLoading(true);
    try {
      const res = await emailApi.getMessages(params);
      let data = res.data.results || res.data || [];
      let total = typeof res.data.count === 'number' ? res.data.count : data.length;
      // Keep spam-matching mail out of the Inbox (it lives in the Spam folder).
      if (folder === 'inbox') {
        const before = data.length;
        data = data.filter(m => !isSpam(m, spamRules));
        total = Math.max(0, total - (before - data.length));
      }
      data = data.map(message =>
        getMessageKeys(message).some(key => readStateOverridesRef.current.has(key))
          ? { ...message, is_read: true, unread_count: 0 }
          : message
      );
      if (append) {
        setMessages(prev => [...prev, ...data]);
      } else {
        setMessages(data);
      }
      hasMoreRef.current = data.length === MESSAGES_PAGE_SIZE;
      pageRef.current = page;
      setMessagesPage(page);
      setMessagesTotal(total);
    } catch (err) {
      console.error('Failed to fetch messages', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    setLoadingMore(true);
    await fetchMessages(activeFolder, activeAccount?.account_id, pageRef.current + 1, true);
    setLoadingMore(false);
    loadingRef.current = false;
  }, [fetchMessages, activeFolder, activeAccount]);

  // Jump to a specific page (Next/Prev buttons) — replaces the visible list.
  const goToPage = useCallback((page) => {
    if (loadingRef.current) return;
    const p = Math.max(1, page || 1);
    fetchMessages(activeFolder, activeAccount?.account_id, p, false);
  }, [fetchMessages, activeFolder, activeAccount]);

  const fetchStats = useCallback(async accountId => {
    try {
      const res = await emailApi.getStats(accountId);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats', err);
    }
  }, []);

  const fetchDrafts = useCallback(async () => {
    try {
      const res = await emailApi.getDrafts();
      setDrafts(res.data.results || res.data || []);
    } catch (err) {
      console.error('Failed to fetch drafts', err);
    }
  }, []);

  const syncNow = useCallback(async (accountId) => {
    const id = accountId || activeAccount?.account_id;
    if (!id) return;
    showToast('Syncing all folders…', 'success');
    try {
      // Pull every folder (Inbox, Sent, Drafts, Junk, Trash, Archive), then
      // refresh the current view, the stats, and the folder list/counts.
      const res = await emailApi.syncAllEmails({ account_id: id });
      // The user may have switched mailboxes while this was in flight — don't
      // let a slow sync for the old mailbox overwrite the new one's view.
      if (activeAccountIdRef.current !== id) return;
      const synced = res?.data?.synced || 0;
      fetchMessages(activeFolderRef.current, id);
      fetchStats(id);
      fetchFolders(id);
      showToast(
        synced > 0
          ? `Synced ${synced} message${synced === 1 ? '' : 's'} across your folders.`
          : 'All folders are up to date.',
        'success'
      );
    } catch (err) {
      if (activeAccountIdRef.current !== id) return;
      const msg = err?.response?.data?.message
        || "Couldn't sync the mailbox. Check your Mailbox settings.";
      showToast(msg, 'error');
    }
  }, [activeAccount, activeFolder, fetchMessages, fetchStats, fetchFolders, showToast]);

  // Apply a saved/updated account (from Mailbox settings) and re-sync it.
  const applyAccount = useCallback((acct) => {
    if (!acct) return;
    readStateOverridesRef.current.clear();
    setAccounts(prev => {
      const rest = prev.filter(a => a.account_id !== acct.account_id);
      return [acct, ...rest];
    });
    setActiveAccount(acct);
    syncedAccountsRef.current.delete(acct.account_id);
  }, []);

  const removeAccount = useCallback((accountId) => {
    if (!accountId) return;

    setAccounts(prev => prev.filter(account => account.account_id !== accountId));
    setActiveAccount(current => {
      if (current?.account_id !== accountId) return current;
      activeAccountIdRef.current = null;
      return null;
    });
    setFolders(DEFAULT_FOLDERS);
    setLabels([]);
    setMessages([]);
    setMessagesTotal(0);
    setMessagesPage(1);
    setStats({ total: 0, unread: 0, flagged: 0, starred: 0 });
    setActiveMessage(null);
    setSelectedMessages(new Set());
    readStateOverridesRef.current.clear();
    syncedAccountsRef.current.delete(accountId);
  }, []);

  const selectFolder = useCallback(folder => {
    setActiveFolder(folder);
    activeFolderRef.current = folder;
    setActiveMessage(null);
    setSelectedMessages(new Set());
    setMessages([]);      // clear immediately so the list shows its loading shimmer
    setLoading(true);
    pageRef.current = 1;
    hasMoreRef.current = true;
  }, []);

  // Deep-sync the opened folder in the background so ALL of its messages fill in
  // (not just the newest batch). Refreshes the list when done — but only if the
  // user is still on that exact account+folder, so a slow sync for one mailbox
  // can never overwrite what a different mailbox (or a different folder) is
  // currently showing.
  const syncActiveFolder = useCallback(async (folder, accountId) => {
    const id = accountId || activeAccount?.account_id;
    if (!id || !folder) return;
    if (folder === 'starred' || folder === 'snoozed' || folder === 'spam') return; // virtual folders
    const canSync = activeAccount?.protocol === 'ews' || !!activeAccount?.imap_server;
    if (!canSync) return;

    // Guard applied both before starting the network round-trip and again
    // before every write, so this specific call can never clobber a view the
    // user has since navigated away from (a different folder OR a different
    // mailbox — folder name alone isn't enough, since two mailboxes can both
    // be sitting on "inbox" at once).
    const isCurrent = () => activeFolderRef.current === folder && activeAccountIdRef.current === id;

    const key = `${id}:${folder}`;
    if (inFlightSyncRef.current.has(key)) return; // already syncing this exact target
    const lastRun = lastDeepSyncRef.current.get(key) || 0;
    if (Date.now() - lastRun < DEEP_SYNC_COOLDOWN_MS) {
      // Synced very recently (e.g. rapid back-and-forth folder clicks) — the
      // list already shows what's stored locally via fetchMessages, so skip
      // hammering the mail server again for data we just pulled.
      return;
    }

    // Inbox: pull ALL of its messages (in batches, until drained). Every other
    // folder: only the newest 50. Per the requested behaviour.
    const isInbox = folder === 'inbox';
    const BATCH = isInbox ? 500 : 50;
    const MAX_ROUNDS = isInbox ? 200 : 1; // inbox keeps going; others one batch

    inFlightSyncRef.current.add(key);
    setFolderSyncing(true);
    try {
      let round = 0;
      while (round < MAX_ROUNDS) {
        round += 1;
        if (!isCurrent()) break; // user navigated away (folder OR mailbox)
        const res = await emailApi.syncFolder({ account_id: id, folder, limit: BATCH });
        const synced = res?.data?.synced || 0;
        if (synced > 0 && isCurrent()) {
          await fetchMessages(folder, id, 1, false);
        }
        if (synced === 0) break; // nothing new left to download
      }
      lastDeepSyncRef.current.set(key, Date.now());
      if (isCurrent()) {
        fetchStats(id);
        fetchFolders(id);
      }
    } catch (err) {
      // Silent — the folder still shows whatever is already stored locally.
    } finally {
      inFlightSyncRef.current.delete(key);
      setFolderSyncing(false);
    }
  }, [activeAccount, fetchMessages, fetchStats, fetchFolders]);

  const selectMessage = useCallback(async msg => {
    const id = msg.id || msg.message_id;
    setActiveMessage(msg);

    try {
      const res = await emailApi.getMessage(id);
      const email = {
        ...msg,
        ...(res?.data || {}),
      };
      const responseId = res?.data?.id ?? res?.data?.message_id ?? id;
      const responseIsRead = res?.data?.is_read ?? email.is_read;
      const responseKeys = [
        ...new Set([...getMessageKeys(msg), ...getMessageKeys(email)]),
      ];
      if (responseIsRead === true) {
        responseKeys.forEach(key => readStateOverridesRef.current.add(key));
      } else {
        responseKeys.forEach(key => readStateOverridesRef.current.delete(key));
      }
      setActiveMessage(prev =>
        prev && (prev.id === msg.id || prev.message_id === msg.message_id)
          ? email
          : prev
      );

      setMessages(items =>
        items.map(item => {
          const matchesClicked =
            item === msg ||
            (msg.id != null && item.id === msg.id) ||
            (msg.message_id != null && item.message_id === msg.message_id);
          const matchesResponse = getMessageKeys(item).some(key =>
            responseKeys.includes(key) || key === String(responseId)
          );
          if (!matchesClicked && !matchesResponse) return item;

          return {
            ...item,
            is_read: responseIsRead,
            ...(responseIsRead === true ? { unread_count: 0 } : {}),
          };
        })
      );

      if (responseIsRead === true) {
        await fetchStats(activeAccount?.account_id);
      }
    } catch {}
  }, [fetchStats, activeAccount]);

  const clearReadStateOverride = useCallback(message => {
    getMessageKeys(message).forEach(key => readStateOverridesRef.current.delete(key));
  }, []);

  const toggleSelect = useCallback(id => {
    setSelectedMessages(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    if (selectedMessages.size === messages.length) {
      setSelectedMessages(new Set());
    } else {
      setSelectedMessages(new Set(messages.map(m => m.id || m.message_id)));
    }
  }, [messages, selectedMessages]);

  const handleBulkAction = useCallback(async (action, extra = {}) => {
    const ids = Array.from(selectedMessages);
    if (ids.length === 0) return;
    try {
      await emailApi.bulkAction({ action, message_ids: ids, ...extra });
      ids.forEach(id => {
        const key = String(id);
        if (action === 'unread') readStateOverridesRef.current.delete(key);
        if (action === 'read') readStateOverridesRef.current.add(key);
      });
      showToast(`Marked as ${action}`, 'success');
      fetchMessages(activeFolder, activeAccount?.account_id);
      setSelectedMessages(new Set());
    } catch (err) {
      showToast('Action failed', 'error');
    }
  }, [selectedMessages, fetchMessages, activeFolder, activeAccount, showToast]);

  const handleToggleStar = useCallback(async msg => {
    try {
      await emailApi.toggleStar(msg.id || msg.message_id);
      setMessages(prev =>
        prev.map(m =>
          (m.id === msg.id || m.message_id === msg.message_id)
            ? { ...m, is_starred: !m.is_starred }
            : m
        )
      );
    } catch {}
  }, []);

  const handleToggleFlag = useCallback(async msg => {
    try {
      await emailApi.toggleFlag(msg.id || msg.message_id);
      setMessages(prev =>
        prev.map(m =>
          (m.id === msg.id || m.message_id === msg.message_id)
            ? { ...m, is_flagged: !m.is_flagged }
            : m
        )
      );
    } catch {}
  }, []);

  const handleMove = useCallback(async (msg, folderId) => {
    const id = resolveId(msg);
    if (!id) return;
    try {
      await emailApi.moveMessage(id, folderId);
      showToast('Message moved', 'success');
      fetchMessages(activeFolder, activeAccount?.account_id);
    } catch {}
  }, [fetchMessages, activeFolder, activeAccount, showToast]);

  const resolveId = msg => (msg && typeof msg === 'object' ? msg.id || msg.message_id : msg);

  const handleDelete = useCallback(async msg => {
    const id = resolveId(msg);
    if (!id) return;
    try {
      await emailApi.bulkAction({ action: 'delete', message_ids: [id] });
      showToast('Message moved to trash', 'success');
      setActiveMessage(null);
      fetchMessages(activeFolder, activeAccount?.account_id);
    } catch {}
  }, [fetchMessages, activeFolder, activeAccount, showToast]);

  const handlePermanentDelete = useCallback(async msg => {
    const id = resolveId(msg);
    if (!id) return;
    try {
      await emailApi.bulkAction({ action: 'permanent_delete', message_ids: [id] });
      showToast('Message permanently deleted', 'success');
      setActiveMessage(null);
      fetchMessages(activeFolder, activeAccount?.account_id);
    } catch {}
  }, [fetchMessages, activeFolder, activeAccount, showToast]);

  const handleArchive = useCallback(async msg => {
    const id = resolveId(msg);
    if (!id) return;
    try {
      await emailApi.bulkAction({ action: 'archive', message_ids: [id] });
      showToast('Message archived', 'success');
      setActiveMessage(null);
      fetchMessages(activeFolder, activeAccount?.account_id);
    } catch {}
  }, [fetchMessages, activeFolder, activeAccount, showToast]);

  const handleSearch = useCallback(async (query, filters = {}) => {
    setSearchQuery(query);
    if (!query) {
      setSearchResults(null);
      return;
    }
    try {
      const res = await emailApi.searchMessages({ query, ...filters });
      setSearchResults(res.data);
    } catch {
      setSearchResults({ results: [], count: 0 });
    }
  }, []);

  const openCompose = useCallback((data = null) => {
    setComposeData(data);
    setReplyData(null);
    setForwardData(null);
    setComposeOpen(true);
  }, []);

  const openReply = useCallback((msg, type = 'reply') => {
    setReplyData({ message: msg, type });
    setComposeData(null);
    setForwardData(null);
    setComposeOpen(true);
  }, []);

  const openForward = useCallback(msg => {
    setForwardData({ message: msg });
    setComposeData(null);
    setReplyData(null);
    setComposeOpen(true);
  }, []);

  const closeCompose = useCallback(() => {
    setComposeOpen(false);
    setComposeData(null);
    setReplyData(null);
    setForwardData(null);
  }, []);

  const handleContextMenu = useCallback((e, msg) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, message: msg });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    if (activeAccount) {
      fetchFolders(activeAccount.account_id);
      fetchLabels(activeAccount.account_id);
      fetchStats(activeAccount.account_id);
    }
  }, [activeAccount, fetchFolders, fetchLabels, fetchStats]);

  // Pull the user's real inbox from their mail server once per account, so the
  // mailbox opens with their actual messages by default. This sync can take a
  // while for a large mailbox — if the user has since switched to a different
  // mailbox before it resolves, its (now stale) result must never overwrite
  // whatever that other mailbox is currently showing. Every write below is
  // guarded on activeAccountIdRef so each mailbox's sync can only ever affect
  // its own view, never bleed into another one.
  useEffect(() => {
    const id = activeAccount?.account_id;
    // IMAP accounts need an explicit server; EWS accounts can auto-discover
    // one from the email address, so ews_server is allowed to be blank.
    const canSync = activeAccount?.protocol === 'ews' || !!activeAccount?.imap_server;
    if (!id || !canSync) return;
    if (syncedAccountsRef.current.has(id)) return;
    syncedAccountsRef.current.add(id);
    (async () => {
      try {
        const res = await emailApi.syncAllEmails({ account_id: id });
        if (activeAccountIdRef.current !== id) return; // switched mailboxes meanwhile
        if (res?.data?.synced) {
          fetchMessages(activeFolderRef.current, id);
          fetchStats(id);
        }
        fetchFolders(id);
      } catch (err) {
        if (activeAccountIdRef.current !== id) return;
        showToast(
          "Couldn't reach your mail server. Check the IMAP settings/password (Outlook/Office 365 may require an app password or OAuth).",
          'error'
        );
      }
    })();
  }, [activeAccount, activeFolder, fetchMessages, fetchStats, fetchFolders, showToast]);

  // Background-sync every OTHER connected mailbox concurrently, not just the
  // one currently open, so N mailboxes sync in parallel instead of only
  // starting mailbox 2's sync once mailbox 1's finishes. These never touch
  // `messages`/`stats`/`folders` (those are the CURRENTLY OPEN mailbox's
  // view) — a background mailbox only needs its data sitting ready in the
  // database, so opening it later is instant; the per-account effect above
  // (and the folder-open effect) handle populating the view once the user
  // actually switches to it. Dedup is shared with that effect via
  // syncedAccountsRef, so a given account is never synced by both paths.
  //
  // Concurrency is capped rather than firing all N mailboxes at once: each
  // mailbox's own sync already runs up to 4 folders in parallel server-side,
  // so an unbounded fan-out here would multiply that across every mailbox
  // (5 mailboxes -> up to 20 simultaneous connections to the mail server) —
  // capping keeps total concurrent load bounded and predictable regardless
  // of how many mailboxes are connected.
  const BACKGROUND_MAILBOX_CONCURRENCY = 2;
  useEffect(() => {
    const queue = accounts.filter(acct => {
      const id = acct?.account_id;
      const canSync = acct?.protocol === 'ews' || !!acct?.imap_server;
      return id && canSync && !syncedAccountsRef.current.has(id);
    });
    if (!queue.length) return;

    let cancelled = false;
    let cursor = 0;
    const runNext = () => {
      if (cancelled || cursor >= queue.length) return;
      const acct = queue[cursor++];
      const id = acct.account_id;
      if (syncedAccountsRef.current.has(id)) { runNext(); return; } // claimed by the other effect meanwhile
      syncedAccountsRef.current.add(id);
      emailApi.syncAllEmails({ account_id: id })
        .catch(() => {
          // Silent — this mailbox just stays un-synced until the user opens
          // it (or the next background pass), same as any other background task.
        })
        .finally(runNext); // pull the next queued mailbox into this now-free slot
    };
    for (let i = 0; i < BACKGROUND_MAILBOX_CONCURRENCY; i += 1) runNext();

    return () => { cancelled = true; };
  }, [accounts]);

  // ── New-mail detector ─────────────────────────────────────────────────────
  // Watch the inbox message list directly and pop an Outlook-style desktop toast
  // whenever a genuinely new UNREAD message lands on top. This is independent of
  // how the mail arrived (background poll, always-on server worker, or a manual
  // refetch), so the toast is reliable.
  const knownKeysRef = useRef(new Set());
  const primedRef = useRef(false);

  // Reset the baseline whenever the folder or account changes so switching to a
  // folder never toasts its whole contents.
  useEffect(() => {
    knownKeysRef.current = new Set();
    primedRef.current = false;
  }, [activeFolder, activeAccount?.account_id]);

  useEffect(() => {
    if (activeFolder !== 'inbox') return;
    const keyOf = (m) => m.id || m.message_id;
    const keys = messages.map(keyOf);

    // First population after a reset just seeds the baseline — no toast.
    if (!primedRef.current) {
      knownKeysRef.current = new Set(keys);
      if (messages.length) primedRef.current = true;
      return;
    }

    const topKey = keys[0];
    const topIsFresh = topKey && !knownKeysRef.current.has(topKey);
    const freshMsgs = messages.filter((m) => !knownKeysRef.current.has(keyOf(m)));
    // Rebuild the baseline to the current view (covers pagination adding older mail).
    knownKeysRef.current = new Set(keys);

    // Only notify when brand-new mail arrives at the TOP (i.e. it was prepended),
    // not when older mail is paged in at the bottom.
    if (topIsFresh) {
      const unreadFresh = freshMsgs.filter((m) => !m.is_read);
      const pick = unreadFresh[0] || (messages[0] && !messages[0].is_read ? messages[0] : null);
      if (pick) {
        setNewMailNotice({
          msg: pick,
          key: keyOf(pick),
          count: unreadFresh.length || 1,
          ts: Date.now(),
        });
      }
    }
  }, [messages, activeFolder]);

  // Keep the mailbox live: quietly poll the mail server for new incoming mail
  // every 60s while a mailbox is open, so messages arrive on their own without
  // the user pressing "Sync mailbox". The header scan is cheap and only new
  // messages are downloaded, so this stays light.
  useEffect(() => {
    const id = activeAccount?.account_id;
    const canSync = activeAccount?.protocol === 'ews' || !!activeAccount?.imap_server;
    if (!id || !canSync) return undefined;

    let cancelled = false;
    let inFlight = false;
    const poll = async () => {
      if (inFlight || document.hidden) return;
      inFlight = true;
      try {
        // Best-effort: nudge our own sync (harmless if the always-on server
        // worker already pulled the mail — it just returns "nothing new").
        await emailApi.syncEmails({ account_id: id, folder: 'INBOX' }).catch(() => {});
        if (cancelled) return;
        // Only live-merge new mail when viewing the first page; if the user has
        // paged forward with Next, don't disturb the page they're reading.
        if (pageRef.current !== 1) return;

        // Then always refresh the newest page from the DB and merge in anything
        // new, so mail shows up no matter who synced it (our poll OR the server
        // worker). Merging preserves already-loaded older messages and scroll.
        const folderParam = activeFolder === 'starred' ? undefined : activeFolder;
        const params = { page: 1, page_size: MESSAGES_PAGE_SIZE, account: id };
        if (folderParam) params.folder = folderParam;
        if (activeFolder === 'inbox') { params.deleted = 'false'; params.spam = 'false'; }
        if (activeFolder === 'trash') params.deleted = 'true';
        if (activeFolder === 'junk') params.spam = 'true';
        if (activeFolder === 'starred') params.starred = 'true';

        const res = await emailApi.getMessages(params);
        if (cancelled) return;
        const fresh = res?.data?.results || res?.data || [];
        if (!fresh.length) return;

        const keyOf = (m) => m.id || m.message_id;
        let added = 0;
        setMessages((prev) => {
          const seen = new Set(prev.map(keyOf));
          const newOnes = fresh.filter((m) => !seen.has(keyOf(m)));
          added = newOnes.length;
          if (!added) return prev;
          return [...newOnes, ...prev];
        });
        // The "new mail" toast is fired by a dedicated messages watcher below,
        // so it works no matter how the mail arrives (this poll, the server
        // worker, or a refetch) — not only through this code path.
        if (added) fetchStats(id);
      } catch (err) {
        // Silent on background polls — never interrupt the user with a toast.
      } finally {
        inFlight = false;
      }
    };
    const timer = setInterval(poll, 20000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeAccount, activeFolder, fetchMessages, fetchStats]);

  useEffect(() => {
    if (activeAccount) {
      // Update both refs together, synchronously, before kicking off any
      // network call — this is what lets syncActiveFolder tell "still the
      // same mailbox+folder the user is looking at" from "stale, navigated
      // away" for any sync still in flight from before this change.
      activeFolderRef.current = activeFolder;
      activeAccountIdRef.current = activeAccount.account_id;
      fetchMessages(activeFolder, activeAccount.account_id);
      // Kick off a deep sync of this folder so all its messages fill in.
      syncActiveFolder(activeFolder, activeAccount.account_id);
    }
  }, [activeFolder, activeAccount, fetchMessages, syncActiveFolder]);

  return {
    accounts, activeAccount, setActiveAccount,
    folders, activeFolder, selectFolder,
    messages, loading, loadingMore, loadMore, hasMoreRef,
    messagesPage, messagesTotal, messagesPageSize: MESSAGES_PAGE_SIZE, goToPage,
    selectedMessages, toggleSelect, selectAll,
    activeMessage, selectMessage,
    stats, labels,
    drafts, fetchDrafts,
    composeOpen, composeData, replyData, forwardData,
    openCompose, openReply, openForward, closeCompose,
    searchOpen, setSearchOpen, searchQuery, searchResults, handleSearch,
    showReadingPane, setShowReadingPane, splitPane, setSplitPane,
    contextMenu, closeContextMenu, handleContextMenu,
    handleToggleStar, handleToggleFlag, handleBulkAction,
    handleMove, handleDelete, handlePermanentDelete, handleArchive,
    toast, showToast,
    newMailNotice, setNewMailNotice,
    folderSyncing,
    fetchMessages, fetchStats, fetchFolders, setActiveMessage,
    syncNow, applyAccount, removeAccount, clearReadStateOverride,
  };
}
